// /users/$user_id — player profile + Wave 3 mutating actions
// Spec: docs/plans/admin-tooling.md §1.1, D-62

import { useParams, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { ActionModal, type ActionDef } from '@/components/admin/action-modal';
import { Button, Panel, PanelHeader, PageHeader, ErrorChip } from '@awakenedlands/ui';
import { StatusPill, type PillStatus } from '@/components/admin/status-pill';

// ── Response shape (mirrors GET /api/admin/users/:user_id) ──────────────────

interface UserRow {
  user_id: number;
  username: string;
  login_name: string;
  email: string;
  activated: number;
  active: number;
  user_level: number;
  level: number;
  experience: number;
  cash: number;
  credits: number;
  locked_at: number | null;
  locked_reason: string | null;
  created_at: number | null;
  last_ip: string | null;
}

interface FedjailRow {
  days: number;
  staff_id: number;
  reason: string;
  set_at: number | null;
}

interface TwoFa {
  totp_enrolled: boolean;
  webauthn_count: number;
}

interface SessionRow {
  sid_hash: string;
  created_at: number;
  last_seen_at: number;
  expires_at: number;
  last_ip: string;
  user_agent: string;
}

interface AuditRow {
  id: number;
  staff_id: number;
  action: string;
  payload: unknown;
  ip: string;
  created_at: number;
}

interface ProfileResponse {
  ok: boolean;
  user: UserRow;
  fedjail: FedjailRow | null;
  two_fa: TwoFa;
  sessions: SessionRow[];
  recent_audit: AuditRow[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtUnix(ts: number | null | undefined): string {
  if (ts === null || ts === undefined) return '—';
  return new Date(ts * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function fmtCash(n: number): string {
  return '¥' + n.toLocaleString();
}

function payloadSummary(payload: unknown): string {
  if (payload === null || payload === undefined) return '—';
  if (typeof payload === 'string') return payload.slice(0, 80);
  try {
    const s = JSON.stringify(payload);
    return s.slice(0, 80) + (s.length > 80 ? '…' : '');
  } catch {
    return '—';
  }
}

// ── Simple key-value row ──────────────────────────────────────────────────────

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="w-36 shrink-0 text-xs font-medium text-ink-low">{label}</dt>
      <dd className="text-sm text-ink-high break-all">{value ?? '—'}</dd>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type LoadState = 'loading' | 'done' | 'not_found' | 'error';

export function UserProfilePage() {
  const { user_id } = useParams({ strict: false }) as { user_id?: string };
  const navigate = useNavigate();

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeAction, setActiveAction] = useState<ActionDef | null>(null);

  const loadProfile = useCallback(async () => {
    if (!user_id) {
      setLoadState('not_found');
      return;
    }

    setLoadState('loading');

    const res = await apiFetch<ProfileResponse>(`/admin/users/${user_id}`);

    if (res.status === 404) {
      setLoadState('not_found');
      return;
    }

    if (!res.ok) {
      setLoadState('error');
      setErrorMsg('Failed to load player profile.');
      return;
    }

    setProfile(res.data);
    setLoadState('done');
  }, [user_id]);

  const reload = useCallback(() => { void loadProfile(); }, [loadProfile]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loadState === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
      </div>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────────

  if (loadState === 'not_found') {
    return (
      <div className="space-y-4">
        <button
          type="button"
          className="text-sm text-ink-low hover:text-ink-mid underline"
          onClick={() => void navigate({ to: '/users' })}
        >
          ← Players
        </button>
        <p className="text-sm text-ink-mid">Player not found.</p>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────

  if (loadState === 'error' || !profile) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          className="text-sm text-ink-low hover:text-ink-mid underline"
          onClick={() => void navigate({ to: '/users' })}
        >
          ← Players
        </button>
        <ErrorChip>{errorMsg || 'An error occurred.'}</ErrorChip>
      </div>
    );
  }

  const { user, fedjail, two_fa, sessions, recent_audit } = profile;

  // ── Status pill ─────────────────────────────────────────────────────────────

  const pillStatus: PillStatus =
    user.locked_at !== null ? 'locked' : user.active ? 'active' : 'inactive';

  // ── Action buttons ──────────────────────────────────────────────────────────

  function ActionButton({
    intent,
    label,
    variant,
    visible,
  }: {
    intent: string;
    label: string;
    variant: 'danger' | 'ghost';
    visible: boolean;
  }) {
    if (!visible) return null;
    return (
      <Button
        key={intent}
        type="button"
        variant={variant}
        onClick={() =>
          setActiveAction({ intent, label, userId: user.user_id, userLastIp: user.last_ip })
        }
      >
        {label}
      </Button>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Back link */}
      <button
        type="button"
        className="text-sm text-ink-low hover:text-ink-mid underline"
        onClick={() => void navigate({ to: '/users' })}
      >
        ← Players
      </button>

      <PageHeader
        title={user.username}
        description={<span className="font-mono text-ink-low">{user.login_name}</span>}
      />

      <div className="space-y-4">
        {/* Account card */}
        <Panel flush>
          <PanelHeader>Account</PanelHeader>
          <div className="px-5 py-4">
            <dl className="space-y-2">
              <KV label="User ID" value={<span className="font-mono tabular-nums">{user.user_id}</span>} />
              <KV label="Login name" value={<span className="font-mono">{user.login_name}</span>} />
              <KV label="Username" value={user.username} />
              <KV label="Email" value={user.email} />
              <KV label="Level" value={<span className="tabular-nums">{user.level}</span>} />
              <KV label="Experience" value={<span className="font-mono tabular-nums">{user.experience.toLocaleString()}</span>} />
              <KV label="Cash" value={<span className="font-mono tabular-nums">{fmtCash(user.cash)}</span>} />
              <KV label="Credits" value={<span className="font-mono tabular-nums">{user.credits.toLocaleString()}</span>} />
              <KV label="Status" value={<StatusPill status={pillStatus} />} />
              {user.locked_at !== null && (
                <>
                  <KV label="Locked at" value={<span className="font-mono tabular-nums text-xs">{fmtUnix(user.locked_at)}</span>} />
                  <KV label="Lock reason" value={user.locked_reason} />
                </>
              )}
              <KV label="Created" value={<span className="font-mono tabular-nums text-xs">{fmtUnix(user.created_at)}</span>} />
              <KV label="Last IP" value={<span className="font-mono">{user.last_ip ?? '—'}</span>} />
            </dl>
          </div>
        </Panel>

        {/* Actions section */}
        <Panel flush>
          <PanelHeader>Actions</PanelHeader>
          <div className="px-5 py-4">
            <div className="flex flex-wrap gap-2">
              <ActionButton intent="account-lock"   label="Lock Account"   variant="danger"  visible={user.locked_at === null} />
              <ActionButton intent="account-unlock" label="Unlock Account" variant="ghost"   visible={user.locked_at !== null} />
              <ActionButton intent="fedjail"        label="Fedjail"        variant="danger"  visible={fedjail === null} />
              <ActionButton intent="fedjail-clear"  label="Clear Fedjail"  variant="ghost"   visible={fedjail !== null} />
              <ActionButton intent="force-logout"   label="Force Logout"   variant="danger"  visible={true} />
              <ActionButton intent="password-reset"  label="Password Reset"        variant="ghost"   visible={true} />
              <ActionButton intent="recover-email"   label="Issue Account Recovery" variant="ghost"   visible={true} />
              <ActionButton intent="disable-2fa"    label="Disable 2FA"    variant="danger"  visible={two_fa.totp_enrolled || two_fa.webauthn_count > 0} />
              <ActionButton intent="ip-ban"         label="IP Ban"         variant="danger"  visible={user.last_ip !== null} />
              <ActionButton intent="ip-unban"       label="IP Unban"       variant="ghost"   visible={user.last_ip !== null} />
            </div>
          </div>
        </Panel>

        {/* Fedjail card — only shown when fedjail is non-null */}
        {fedjail !== null && (
          <Panel flush>
            <PanelHeader>Federal Jail</PanelHeader>
            <div className="px-5 py-4">
              <dl className="space-y-2">
                <KV label="Days" value={<span className="tabular-nums">{fedjail.days}</span>} />
                <KV label="Reason" value={fedjail.reason} />
                <KV label="Set by staff ID" value={<span className="tabular-nums">{fedjail.staff_id}</span>} />
                <KV label="Set at" value={<span className="font-mono tabular-nums text-xs">{fmtUnix(fedjail.set_at)}</span>} />
              </dl>
            </div>
          </Panel>
        )}

        {/* 2FA card */}
        <Panel flush>
          <PanelHeader>Two-Factor Authentication</PanelHeader>
          <div className="px-5 py-4">
            <dl className="space-y-2">
              <KV label="TOTP enrolled" value={two_fa.totp_enrolled ? 'Yes' : 'No'} />
              <KV label="WebAuthn credentials" value={<span className="tabular-nums">{two_fa.webauthn_count}</span>} />
            </dl>
          </div>
        </Panel>

        {/* Active sessions card */}
        <Panel flush>
          <PanelHeader>Active Sessions</PanelHeader>
          <div className="px-5 py-4">
            {sessions.length === 0 ? (
              <p className="text-sm text-ink-low">No active sessions.</p>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-ink-low">
                      <tr>
                        <th className="pb-2 pr-4 font-medium">Last seen</th>
                        <th className="pb-2 pr-4 font-medium">IP</th>
                        <th className="pb-2 pr-4 font-medium">User agent</th>
                        <th className="pb-2 font-medium">Expires</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-divider">
                      {sessions.map((s) => (
                        <tr key={s.sid_hash}>
                          <td className="py-2 pr-4 whitespace-nowrap font-mono tabular-nums">{fmtUnix(s.last_seen_at)}</td>
                          <td className="py-2 pr-4 font-mono">{s.last_ip}</td>
                          <td className="py-2 pr-4 max-w-[200px] truncate" title={s.user_agent}>
                            {s.user_agent}
                          </td>
                          <td className="py-2 whitespace-nowrap font-mono tabular-nums">{fmtUnix(s.expires_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="flex flex-col gap-3 sm:hidden">
                  {sessions.map((s) => (
                    <div key={s.sid_hash} className="rounded-sm border border-border bg-muted p-3 text-xs space-y-1">
                      <div><span className="font-medium text-ink-mid">Last seen: </span><span className="font-mono tabular-nums">{fmtUnix(s.last_seen_at)}</span></div>
                      <div><span className="font-medium text-ink-mid">IP: </span><span className="font-mono">{s.last_ip}</span></div>
                      <div className="truncate"><span className="font-medium text-ink-mid">Agent: </span>{s.user_agent}</div>
                      <div><span className="font-medium text-ink-mid">Expires: </span><span className="font-mono tabular-nums">{fmtUnix(s.expires_at)}</span></div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Panel>

        {/* Recent staff actions card */}
        <Panel flush>
          <PanelHeader>Recent Staff Actions</PanelHeader>
          <div className="px-5 py-4">
            {recent_audit.length === 0 ? (
              <p className="text-sm text-ink-low">No recent staff actions.</p>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left text-ink-low">
                      <tr>
                        <th className="pb-2 pr-4 font-medium">Time</th>
                        <th className="pb-2 pr-4 font-medium">Action</th>
                        <th className="pb-2 pr-4 font-medium">Staff ID</th>
                        <th className="pb-2 font-medium">Payload</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-divider">
                      {recent_audit.map((a) => (
                        <tr key={a.id}>
                          <td className="py-2 pr-4 whitespace-nowrap font-mono tabular-nums">{fmtUnix(a.created_at)}</td>
                          <td className="py-2 pr-4 font-mono">{a.action}</td>
                          <td className="py-2 pr-4 tabular-nums">{a.staff_id}</td>
                          <td className="py-2 max-w-[200px] truncate font-mono text-ink-low">
                            {payloadSummary(a.payload)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="flex flex-col gap-3 sm:hidden">
                  {recent_audit.map((a) => (
                    <div key={a.id} className="rounded-sm border border-border bg-muted p-3 text-xs space-y-1">
                      <div><span className="font-medium text-ink-mid">Time: </span><span className="font-mono tabular-nums">{fmtUnix(a.created_at)}</span></div>
                      <div><span className="font-medium text-ink-mid">Action: </span><span className="font-mono">{a.action}</span></div>
                      <div><span className="font-medium text-ink-mid">Staff ID: </span><span className="tabular-nums">{a.staff_id}</span></div>
                      <div className="truncate"><span className="font-medium text-ink-mid">Payload: </span>{payloadSummary(a.payload)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Panel>
      </div>

      {/* ActionModal overlay */}
      {activeAction !== null && (
        <ActionModal
          action={activeAction}
          onDone={() => { setActiveAction(null); reload(); }}
          onCancel={() => setActiveAction(null)}
        />
      )}
    </div>
  );
}
